/**
 * Plan gating helpers.
 *
 * Plans come in two flavours:
 *  - "core" plans (Free / Business / Premium) — the standard listing tiers.
 *  - "boost" plans (Sponsored Listing / Featured Listing) — short-term,
 *    renewable placement plans that also guarantee a spot in the public
 *    sponsored / featured blocks.
 *
 * A "paid" listing is any business on a paid plan (Business, Premium or either
 * boost plan). Free listings keep the baseline experience; paid features
 * (live opening-hours status, public share counter, ...) unlock only for them.
 */
const PAID_PLAN_SLUGS = [
  "business",
  "premium",
  "sponsored-listing",
  "featured-listing",
];
const PREMIUM_PLAN_SLUG = "premium";

/** True when the given plan slug unlocks paid-only listing features. */
export function isPaidPlan(plan?: string | null): boolean {
  if (!plan) return false;
  const slug = plan.trim().toLowerCase();
  if (PAID_PLAN_SLUGS.includes(slug)) return true;
  // Boost plans are also paid — match them semantically so admin-created
  // variants ("sponsored", "sponsored-plan", ...) still count.
  return slug.includes("sponsor") || slug.includes("featur");
}

/** True when the given plan slug is the Premium plan. */
export function isPremiumPlan(plan?: string | null): boolean {
  if (!plan) return false;
  return plan.trim().toLowerCase() === PREMIUM_PLAN_SLUG;
}

/**
 * True when the plan grants sponsored placement (homepage Sponsored block,
 * district & taluk pages, and the top of its selected category pages).
 */
export function isSponsoredPlan(plan?: string | null): boolean {
  if (!plan) return false;
  return plan.trim().toLowerCase().includes("sponsor");
}

/**
 * True when the plan grants featured placement (homepage Featured block and,
 * on category pages, directly after the sponsored listings).
 */
export function isFeaturedPlan(plan?: string | null): boolean {
  if (!plan) return false;
  const slug = plan.trim().toLowerCase();
  if (slug.includes("sponsor")) return false;
  return slug.includes("featur");
}

/** True for the short-term placement plans (Sponsored Listing / Featured Listing). */
export function isBoostPlan(plan?: string | null): boolean {
  return isSponsoredPlan(plan) || isFeaturedPlan(plan);
}

/** Number of main categories / sub categories, photos and video access a plan unlocks. */
export interface PlanLimits {
  /** How many main categories a listing may be filed under. */
  categories: number;
  /** How many sub categories a listing may pick (0 = none). */
  subcategories: number;
  /** Maximum number of photos a listing may upload. */
  photos: number;
  /** Whether a video tour link can be added. */
  video: boolean;
}

/**
 * Plan → feature limits. Mirrors the numbers advertised on the pricing page:
 *   Free              → 1 category, no sub categories, no video
 *   Business          → 1 main category + 4 sub categories, no video
 *   Premium           → 3 main categories + 6 sub categories, video tour
 *   Featured Listing  → clone of Business (1 main + 4 sub, no video)
 *   Sponsored Listing → clone of Premium  (3 main + 6 sub, video tour)
 */
const PLAN_LIMITS: Record<string, PlanLimits> = {
  free:                { categories: 1, subcategories: 0, photos: 3,  video: false },
  business:            { categories: 1, subcategories: 4, photos: 8,  video: false },
  premium:             { categories: 3, subcategories: 6, photos: 10, video: true },
  "featured-listing":  { categories: 1, subcategories: 4, photos: 8,  video: false },
  "sponsored-listing": { categories: 3, subcategories: 6, photos: 10, video: true },
};

/** Resolve a plan slug (case-insensitive) to its feature limits. */
export function getPlanLimits(plan?: string | null): PlanLimits {
  const slug = (plan ?? "").trim().toLowerCase();
  if (!slug) return PLAN_LIMITS.free;
  if (PLAN_LIMITS[slug]) return PLAN_LIMITS[slug];
  // Semantic fallbacks so admin-created boost plans still behave correctly.
  if (slug.includes("sponsor")) return PLAN_LIMITS["sponsored-listing"];
  if (slug.includes("featur")) return PLAN_LIMITS["featured-listing"];
  return PLAN_LIMITS.free;
}

// ── Content-aware limits ──────────────────────────────────────────────────────
//
// Admins describe a plan's allowances in plain words inside the plan's
// highlights ("List in 3 Main Categories and 6 Sub Categories", "List in One
// Main Category and 4 Sub Categories", …). Instead of keeping a second,
// hardcoded copy of those numbers, we read them straight from the plan content
// so the business form always matches whatever the admin wrote.

/** Words that may stand in for a number inside plan copy. */
const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

/** The `(2 | three | …)` alternation shared by both detectors. */
const COUNT_TOKEN = "(\\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten)";

function toCount(raw: string): number | null {
  const token = raw.trim().toLowerCase();
  if (/^\d+$/.test(token)) return parseInt(token, 10);
  if (token in NUMBER_WORDS) return NUMBER_WORDS[token];
  return null;
}

function clampCount(value: number): number {
  return Math.max(0, Math.min(20, value));
}

/**
 * Pull a category count out of free-form text.
 *
 * `sub` matches "6 Sub Categories" / "4 sub-categories" (the number sits right
 * before "sub"), while `main` matches "3 Main Categories" / "1 Category" but
 * deliberately skips the sub-category phrases because "sub" sits between the
 * number and the word "categories" there. Returns null when nothing is stated.
 */
function extractCount(text: string, kind: "main" | "sub"): number | null {
  if (kind === "sub") {
    if (/no\s+sub[\s-]*categor/i.test(text)) return 0;
    const match = text.match(new RegExp(`${COUNT_TOKEN}\\s*sub[\\s-]*categor`, "i"));
    return match ? toCount(match[1]) : null;
  }
  const match = text.match(new RegExp(`${COUNT_TOKEN}\\s+(?:main\\s+)?categor`, "i"));
  return match ? toCount(match[1]) : null;
}

/**
 * Detect whether free-form plan copy grants a video tour.
 *
 * Any mention of "video" counts ("Add a video tour", "Video tour enabled",
 * "…with video"), unless the copy explicitly denies it ("No video", "without
 * video", "Video not included"). Returns null when the copy says nothing, so
 * the slug-based default can decide.
 */
function extractVideo(text: string): boolean | null {
  if (/\bno\s+video\b|\bwithout\s+video\b|\bvideo\s+(?:not|isn'?t|is not)\b/i.test(text)) {
    return false;
  }
  return /\bvideo\b/i.test(text) ? true : null;
}

/**
 * Pull a photo allowance out of free-form text ("Up to 10 photos",
 * "7 photos gallery", "20 photos"). Returns null when nothing is stated, and 0
 * when the copy explicitly denies photos ("No photos").
 */
function extractPhotos(text: string): number | null {
  if (/\bno\s+photos?\b|\bwithout\s+photos?\b/i.test(text)) return 0;
  const match = text.match(new RegExp(`${COUNT_TOKEN}\\s*photos?\\b`, "i"));
  return match ? toCount(match[1]) : null;
}

/** The minimal slice of a plan the parser needs. */
export interface PlanContent {
  slug?: string | null;
  name?: string | null;
  tagline?: string | null;
  highlights?: string[] | null;
}

/**
 * Resolve a plan's limits from the actual plan content, falling back to the
 * slug-based defaults for anything the copy does not spell out. This keeps the
 * business form in sync with whatever the admin typed on the plan — no code
 * change needed when the numbers change.
 */
export function resolvePlanLimits(plan?: PlanContent | null): PlanLimits {
  const fallback = getPlanLimits(plan?.slug);
  if (!plan) return fallback;

  const parsed = parsePlanContent(plan);

  return {
    // A listing must always have at least one (primary) category.
    categories:
      parsed.categories != null ? Math.max(1, clampCount(parsed.categories)) : fallback.categories,
    subcategories:
      parsed.subcategories != null ? clampCount(parsed.subcategories) : fallback.subcategories,
    photos: parsed.photos != null ? clampCount(parsed.photos) : fallback.photos,
    video: parsed.video != null ? parsed.video : fallback.video,
  };
}

/** Raw values read from plan copy — `null` means the copy never stated it. */
export interface ParsedPlanContent {
  categories: number | null;
  subcategories: number | null;
  photos: number | null;
  video: boolean | null;
}

/**
 * Read the explicit allowances out of a plan's title, tagline and highlights,
 * WITHOUT applying slug-based fallbacks. `null` entries mean the plan copy is
 * silent on that allowance — useful for warning admins about missing numbers.
 */
export function parsePlanContent(plan?: PlanContent | null): ParsedPlanContent {
  const empty: ParsedPlanContent = {
    categories: null,
    subcategories: null,
    photos: null,
    video: null,
  };
  if (!plan) return empty;

  const text = [plan.name, plan.tagline, ...(plan.highlights ?? [])]
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .join(" ");
  if (!text.trim()) return empty;

  return {
    categories: extractCount(text, "main"),
    subcategories: extractCount(text, "sub"),
    photos: extractPhotos(text),
    video: extractVideo(text),
  };
}

/** Known slug → display name. */
const PLAN_LABELS: Record<string, string> = {
  free:                "Free",
  business:            "Business",
  premium:             "Premium",
  "featured-listing":  "Featured Listing",
  "sponsored-listing": "Sponsored Listing",
};

/** Human-readable plan name for a slug ("premium" → "Premium"). */
export function getPlanLabel(plan?: string | null): string {
  const slug = (plan ?? "").trim().toLowerCase();
  if (PLAN_LABELS[slug]) return PLAN_LABELS[slug];
  if (slug.includes("sponsor")) return "Sponsored Listing";
  if (slug.includes("featur")) return "Featured Listing";
  return plan ? String(plan) : "Free";
}